import React from 'react';
import { Box, Typography } from '@mui/material';

/* ── Floating glowing orb ── */

interface FloatingBubbleProps {
  size: number;
  top: string;
  left: string;
  color: string;
  delay: number;
  duration: number;
  animation: string;
}

export const FloatingBubble: React.FC<FloatingBubbleProps> = ({
  size,
  top,
  left,
  color,
  delay,
  duration,
  animation,
}) => (
  <Box
    sx={{
      position: 'absolute',
      top,
      left,
      width: size,
      height: size,
      borderRadius: '50%',
      background: color,
      animation: `${animation} ${duration}s ease-in-out ${delay}s infinite`,
      pointerEvents: 'none',
    }}
  />
);

/* ── Animated chat bubble ── */

interface ChatBubbleProps {
  top: string;
  left: string;
  text: string;
  color: string;
  delay: number;
  flip?: boolean;
}

export const ChatBubble: React.FC<ChatBubbleProps> = ({
  top,
  left,
  text,
  color,
  delay,
  flip,
}) => (
  <Box
    sx={{
      position: 'absolute',
      top,
      left,
      px: 1.8,
      py: 1,
      borderRadius: '14px',
      borderBottomLeftRadius: flip ? 14 : 4,
      borderBottomRightRadius: flip ? 4 : 14,
      background: color,
      animation: `float-slow 5s ease-in-out ${delay}s infinite`,
      pointerEvents: 'none',
      boxShadow: `0 4px 20px ${color}44`,
    }}
  >
    <Typography
      variant="caption"
      sx={{
        color: '#fff',
        fontWeight: 500,
        fontSize: '0.7rem',
        whiteSpace: 'nowrap',
      }}
    >
      {text}
    </Typography>
  </Box>
);

/* ── Twinkling star ── */

interface StarProps {
  top: string;
  left: string;
  size: number;
  delay: number;
}

export const Star: React.FC<StarProps> = ({ top, left, size, delay }) => (
  <Box
    sx={{
      position: 'absolute',
      top,
      left,
      width: size,
      height: size,
      animation: `twinkle 3s ease-in-out ${delay}s infinite`,
      pointerEvents: 'none',
    }}
  >
    <svg viewBox="0 0 24 24" width={size} height={size}>
      <path
        d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 16.8l-6.2 4.5 2.4-7.4L2 9.4h7.6z"
        fill="#7c4dff"
        opacity="0.7"
      />
    </svg>
  </Box>
);

/* ── Ambient glow orb ── */

interface GlowOrbProps {
  size: number;
  top?: string;
  bottom?: string;
  left?: string;
  right?: string;
  color: string;
  blur?: number;
}

export const GlowOrb: React.FC<GlowOrbProps> = ({
  size,
  top,
  bottom,
  left,
  right,
  color,
  blur = 40,
}) => (
  <Box
    sx={{
      position: 'absolute',
      width: size,
      height: size,
      borderRadius: '50%',
      background: `radial-gradient(circle, ${color} 0%, transparent 70%)`,
      top,
      bottom,
      left,
      right,
      filter: `blur(${blur}px)`,
      pointerEvents: 'none',
    }}
  />
);
